import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useTimModalStore, type TimOpenSource } from './useTimModalStore';
import type {
  DispatchMessage,
  SetStatePayload,
  TimUnreadMessages
} from './tim-bridge';
import { useTimUnreadToast } from './useTimUnreadToast';
import { useTimBridge } from './tim-bridge';
import { TimUnreadToast } from './TimUnreadToast';
import { handleDownload } from './download';
import appConfig from '../utils/config';

const log = (msg: string, ...args: unknown[]) =>
  console.log(`[IM-BRIDGE] ${msg}`, ...args);

const TIM_BASE_URL = appConfig.timBaseUrl.replace(/\/$/, '');

function getDownloadErrorText(error: unknown) {
  if (error instanceof Error && error.message.trim()) return error.message;
  return 'Download failed.';
}

type OpenOrderPayload = Extract<
  DispatchMessage['payload'],
  { type: 'OPEN_ORDER' }
>;

function buildOrderDetailPath(payload: OpenOrderPayload) {
  if (!payload.orderId || !payload.campaignId) return '';
  return `/campaign/detail/${encodeURIComponent(
    payload.campaignId
  )}/collaboration/${encodeURIComponent(payload.orderId)}`;
}

function openOrderWindow(targetPath: string, payload: OpenOrderPayload) {
  let opened: Window | null = null;
  try {
    if (payload.targetWindowName) {
      opened = window.open(targetPath, payload.targetWindowName);
    } else {
      opened = window.open(targetPath, '_blank', 'noopener,noreferrer');
    }
  } catch (error) {
    console.warn('[IM-BRIDGE] OPEN_ORDER window.open failed', error);
  }
  if (opened) return;

  try {
    window.location.assign(targetPath);
  } catch (error) {
    console.error('[IM-BRIDGE] OPEN_ORDER fallback navigation failed', error);
    window.location.href = targetPath;
  }
}

export interface ModalShellProps {
  open: boolean;
  mounted: boolean;
  onClose: () => void;
  title: string;
  closeIcon: React.ReactNode;
  children?: React.ReactNode;
}

interface TimModalProps {
  role: 'brand' | 'creator';
  platform: 'pc' | 'h5';
  ModalShell: React.ComponentType<ModalShellProps>;
  title: string;
  closeIcon: React.ReactNode;
  trackFirstOpen?: (payload: {
    source: TimOpenSource;
    role: 'brand' | 'creator';
    platform: 'pc' | 'h5';
  }) => void;
}

/**
 * 核心 IM 弹窗组件，两端代码完全一致，差异全部通过 props 注入。
 *
 * iframe src 只含 platform、lang，不含 token 和 role。
 * 通过 SESSION 同步用户身份和界面配置；业务 API 由宿主鉴权代理。
 */
const TimModal: React.FC<TimModalProps> = ({
  role,
  platform,
  ModalShell,
  title,
  closeIcon,
  trackFirstOpen
}) => {
  const { userId, orgId } = useAuth();
  const { i18n } = useTranslation();
  const {
    showTimModal,
    currentCampaignId,
    currentOrderId,
    openSource,
    openSeq,
    timLoaded,
    timUrl,
    setShowTimModal,
    setTimReady,
    setTimUnreadCount
  } = useTimModalStore();

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const targetOriginRef = useRef(new URL(TIM_BASE_URL).origin);
  const [iframeReady, setIframeReady] = useState(false);
  const [unreadMessages, setUnreadMessages] =
    useState<TimUnreadMessages | null>(null);
  const unreadToast = useTimUnreadToast(unreadMessages, showTimModal, userId);
  useEffect(() => {
    setUnreadMessages(null);
  }, [userId, orgId]);
  const lastDeliveredOpenSeqRef = useRef(0);
  const prevShowTimModalRef = useRef(false);
  const openListRef = useRef(false);

  // ── postMessage 发送函数 ──────────────────────────────────────────

  const sendSetState = useCallback((payload: SetStatePayload) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    log(
      '→ iframe TIM_SET_STATE',
      payload.type,
      payload.type === 'API_RESPONSE'
        ? { ...payload, body: '[redacted]' }
        : payload
    );
    win.postMessage(
      { type: 'TIM_SET_STATE', payload },
      targetOriginRef.current
    );
  }, []);

  const {
    ready: imReady,
    request: requestApi,
    cancel: cancelApi,
    clientReady,
    syncSession,
    reportToastRender5sAck
  } = useTimBridge({
    role,
    platform,
    runtime: 'web',
    lang: i18n.language || 'en-US',
    iframeReady,
    imVisible: showTimModal,
    send: sendSetState
  });

  /** orderId 有值 → 导航到具体会话；无值 → 刷新全局列表 */
  const sendOpen = useCallback(
    (orderId?: string, campaignId?: string) => {
      sendSetState({
        type: 'OPEN',
        orderId,
        campaignId,
        ...(!orderId && openListRef.current ? { view: 'list' as const } : {})
      });
      openListRef.current = false;
    },
    [sendSetState]
  );

  const sendOpenForSeq = useCallback(
    (seq: number, orderId?: string, campaignId?: string) => {
      if (seq > 0 && seq === lastDeliveredOpenSeqRef.current) return;
      if (seq > 0) lastDeliveredOpenSeqRef.current = seq;
      sendOpen(orderId, campaignId);
    },
    [sendOpen]
  );

  const sendLang = useCallback(
    (lang: string) => sendSetState({ type: 'LANG', lang }),
    [sendSetState]
  );

  const sendImVisible = useCallback(
    (visible: boolean) => sendSetState({ type: 'IM_VISIBLE', visible }),
    [sendSetState]
  );

  const sendDownloadResult = useCallback(
    (
      requestId: string | undefined,
      result: { ok: true; canceled?: boolean } | { ok: false; error: string }
    ) => {
      if (!requestId) return;
      sendSetState({
        type: 'DOWNLOAD_RESULT',
        requestId,
        ...result
      });
    },
    [sendSetState]
  );

  // ── 监听 im-ui 发来的 TIM_DISPATCH ─────────────────────────────────

  useEffect(() => {
    const origin = targetOriginRef.current;

    const handler = async (event: MessageEvent) => {
      if (
        event.origin !== origin ||
        event.source !== iframeRef.current?.contentWindow
      )
        return;
      const data = event.data as DispatchMessage | undefined;
      if (data?.type !== 'TIM_DISPATCH') return;

      const { payload } = data;
      if (
        !['UNREAD_MESSAGES', 'API_REQUEST', 'API_CANCEL'].includes(payload.type)
      )
        log('← iframe TIM_DISPATCH', payload.type, payload);

      switch (payload.type) {
        case 'API_REQUEST':
          requestApi(payload);
          break;
        case 'API_CANCEL':
          cancelApi(payload.requestId);
          break;
        case 'CLIENT_READY':
          clientReady(payload.ready);
          break;
        case 'READY': {
          if (iframeReady) syncSession();
          setIframeReady(true);
          break;
        }
        case 'UNREAD_COUNT':
          setTimUnreadCount(Number(payload.count) || 0, payload.details);
          break;
        case 'UNREAD_MESSAGES':
          if (
            payload.snapshot?.userId === userId &&
            Array.isArray(payload.snapshot.items)
          ) {
            setUnreadMessages(payload.snapshot);
          }
          break;
        case 'CLOSE':
          setShowTimModal(false);
          break;
        case 'OPEN_ORDER': {
          const targetPath = buildOrderDetailPath(payload);
          if (!targetPath) {
            console.warn(
              '[IM-BRIDGE] OPEN_ORDER ignored: missing orderId or campaignId',
              payload
            );
            break;
          }
          openOrderWindow(targetPath, payload);
          break;
        }
        case 'DOWNLOAD':
          void Promise.resolve(handleDownload(payload))
            .then((result) =>
              sendDownloadResult(payload.requestId, {
                ok: true,
                canceled: 'canceled' in result ? result.canceled : undefined
              })
            )
            .catch((error) =>
              sendDownloadResult(payload.requestId, {
                ok: false,
                error: getDownloadErrorText(error)
              })
            );
          break;
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [
    userId,
    platform,
    setShowTimModal,
    role,
    currentOrderId,
    currentCampaignId,
    openSeq,
    sendSetState,
    sendOpenForSeq,
    setTimUnreadCount,
    i18n.language,
    showTimModal,
    sendImVisible,
    sendDownloadResult,
    requestApi,
    cancelApi,
    iframeReady,
    syncSession,
    clientReady
  ]);

  // ── 挂载 iframe（裸 URL，无任何参数）────────────────────────────

  const doInitTim = useCallback(() => {
    if (timLoaded) return;
    log('mounting iframe, src:', `${TIM_BASE_URL}/`);
    setTimReady(`${TIM_BASE_URL}/`);
  }, [timLoaded, setTimReady]);

  useEffect(() => {
    if (!showTimModal || timLoaded) return;
    doInitTim();
  }, [showTimModal, timLoaded, doInitTim]);

  // ── 弹窗打开时发送导航 ────────────────────────────────────────────

  useEffect(() => {
    if (!showTimModal || !currentOrderId) return;
    if (imReady) {
      sendOpenForSeq(openSeq, currentOrderId, currentCampaignId || undefined);
    }
  }, [
    showTimModal,
    currentOrderId,
    currentCampaignId,
    openSeq,
    imReady,
    sendOpenForSeq
  ]);

  useEffect(() => {
    if (!showTimModal || currentOrderId) return;
    if (imReady) sendOpenForSeq(openSeq);
  }, [showTimModal, currentOrderId, openSeq, imReady, sendOpenForSeq]);

  useEffect(() => {
    const wasOpen = prevShowTimModalRef.current;
    prevShowTimModalRef.current = showTimModal;
    if (!showTimModal || wasOpen) return;
    trackFirstOpen?.({ source: openSource, role, platform });
  }, [showTimModal, openSource, role, platform, trackFirstOpen]);

  useEffect(() => {
    if (!imReady) return;
    sendLang(i18n.language || 'en-US');
  }, [i18n.language, imReady, sendLang]);

  useEffect(() => {
    if (!timLoaded || !imReady) return;
    sendImVisible(showTimModal);
  }, [timLoaded, showTimModal, imReady, sendImVisible]);

  return (
    <>
      {unreadToast.shown && (
        <TimUnreadToast
          onRender5sAck={reportToastRender5sAck}
          peers={unreadToast.peers}
          role={role}
          language={i18n.language || 'en-US'}
          onDismiss={unreadToast.dismiss}
          onOpen={(item) => {
            unreadToast.dismiss();
            openListRef.current = !item;
            setShowTimModal(
              true,
              item
                ? {
                    orderId: item.orderId,
                    campaignId: item.campaignId,
                    source: 'toast'
                  }
                : { source: 'toast' }
            );
          }}
        />
      )}
      <ModalShell
        open={showTimModal}
        mounted={timLoaded}
        onClose={() => setShowTimModal(false)}
        title={title}
        closeIcon={closeIcon}
      >
        {timLoaded ? (
          <iframe
            ref={iframeRef}
            title="TIM"
            src={timUrl}
            allow="language-detector"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block'
            }}
          />
        ) : null}
      </ModalShell>
    </>
  );
};

export default TimModal;
